import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Animo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { LoQueLee } from '../../components/onboarding/marca/LoQueLee';
import {
  DIALOGO_DEL_LINK,
  finDePalabra,
  LO_QUE_LEE,
  reaccionAlError,
  REACCION_SIN_WEB,
  reaccionAlEnviar,
  reaccionAlLink,
  STICKERS_SALTAN_CON,
  type QueLee,
  type Reaccion,
} from '../../lib/onboardingMarca/dialogo';
import { detectarPlataforma, dominioDe, normalizarLink, type Plataforma } from '../../lib/onboardingMarca/link';
import { iniciarEscaneo } from '../../lib/onboardingMarca/escaneo';
import { prepararEmpresa } from '../../lib/onboardingMarca/marca';
import { guardarEstado, limpiarEstado } from '../../lib/onboardingMarca/estado';
import {
  descartarBorradoresAbandonados,
  DESCRIBIR_MARCA,
  DIALOGO_DE_DESCRIBIR_MARCA,
  DIALOGO_DE_MARCA_NUEVA,
  enDescribirMarca,
  enMarcaNueva,
  MARCA_NUEVA,
  STICKERS_DE_MARCA_NUEVA_SALTAN_CON,
} from '../../lib/onboardingMarca/marcaNueva';

/** Cumbre habla en cada carga; sólo sale escrito si la persona pide menos movimiento. */
function arrancaQuieto(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const ETIQUETA_CORTA: Record<Plataforma, string> = {
  tiendanube: 'Tiendanube',
  shopify: 'Shopify',
  instagram: 'Instagram',
  web: 'Web',
};

/** Cuánto entre un sticker y el siguiente. */
const MS_ENTRE_STICKERS = 160;

/**
 * A1 · El link de la marca (ADR-2026-09-28-onboarding-v2-etapa-1). El único dato que
 * pedimos: con él se crea la empresa (si no existe) y arranca el escaneo, que sigue en
 * el backend mientras la persona pasa al tablero.
 *
 * Lo pide Cumbre (ADR-2026-10-01-onboarding-v2-cumbre-guia): primero termina de hablar y
 * después aparecen el campo y los botones; mientras tanto reacciona a lo que se pega. Sigue
 * en las preguntas (ADR-2026-10-02-onboarding-v2-preguntas-con-cumbre).
 */
export function LinkDeMarca() {
  const navigate = useNavigate();
  // "Añadir nueva marca" usa esta misma pantalla (ADR-2026-10-02-marca-nueva-como-el-onboarding):
  // otro globo, el escaneo de una marca adicional y, después, directo al tablero.
  const { pathname } = useLocation();
  const nueva = enMarcaNueva(pathname);
  // "Describí tu marca" desde la home (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo):
  // como la marca nueva, directo al tablero, pero el escaneo va a la marca DEFAULT.
  const describir = enDescribirMarca(pathname);
  const tramosDelLink = nueva ? DIALOGO_DE_MARCA_NUEVA : describir ? DIALOGO_DE_DESCRIBIR_MARCA : DIALOGO_DEL_LINK;
  /** En qué letra del diálogo empiezan a saltar los stickers. */
  const saltanEn = finDePalabra(
    tramosDelLink,
    'detalle',
    nueva || describir ? STICKERS_DE_MARCA_NUEVA_SALTAN_CON : STICKERS_SALTAN_CON,
  );
  const flujo = nueva ? 'marca_nueva' : describir ? 'describir_marca' : 'onboarding';
  const siguiente = nueva ? MARCA_NUEVA.marca : describir ? DESCRIBIR_MARCA.marca : ONBOARDING_V2.setupPreguntas;
  const [quieto] = useState(arrancaQuieto);
  const dialogo = useDialogo(tramosDelLink, { instantaneo: quieto, arrancarEn: 1000 });
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enfocado, setEnfocado] = useState(false);
  const [dicho, setDicho] = useState<Reaccion | null>(null);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // El campo arranca SIEMPRE vacío, aunque haya un link de una vuelta anterior: volver al
  // paso 1 es empezar de nuevo (ADR-2026-10-02-onboarding-v2-link-en-blanco).

  // Cuando Cumbre termina, entra el formulario y el foco va al campo.
  useEffect(() => {
    if (!dialogo.terminado) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), quieto ? 0 : 450);
    return () => window.clearTimeout(t);
  }, [dialogo.terminado, quieto]);

  const plataforma = detectarPlataforma(valor);
  const esInstagram = plataforma === 'instagram';
  const reaccionDelLink = useMemo(() => reaccionAlLink(plataforma), [plataforma]);
  const reaccion = dicho ?? reaccionDelLink;

  // Un link que se reconoce, festeja; Instagram, duda. Sólo al cambiar de plataforma.
  const plataformaPrevia = useRef<Plataforma | null>(null);
  useEffect(() => {
    if (plataforma === plataformaPrevia.current) return;
    plataformaPrevia.current = plataforma;
    if (!plataforma) return;
    setGesto((g) => ({ tipo: plataforma === 'instagram' ? 'duda' : 'festeja', n: (g?.n ?? 0) + 1 }));
  }, [plataforma]);

  const dudar = (mensaje: string) => {
    setError(mensaje);
    setDicho(reaccionAlError(mensaje, Date.now()));
    setGesto((g) => ({ tipo: 'duda', n: (g?.n ?? 0) + 1 }));
  };

  // Los stickers saltan de a uno cuando Cumbre dice que lee la web (o de una, si se saltea).
  const [saltaron, setSaltaron] = useState(quieto ? LO_QUE_LEE.length : 0);
  const empezaron = dialogo.letras >= saltanEn;
  useEffect(() => {
    if (!empezaron || saltaron >= LO_QUE_LEE.length) return;
    const t = window.setTimeout(() => setSaltaron((n) => n + 1), saltaron === 0 ? 0 : MS_ENTRE_STICKERS);
    return () => window.clearTimeout(t);
  }, [empezaron, saltaron]);
  const visibles = useMemo(() => new Set<QueLee>(LO_QUE_LEE.slice(0, saltaron).map((q) => q.id)), [saltaron]);

  const animo: Animo = enviando ? 'piensa' : enfocado ? 'mira' : 'escucha';

  const enviar = async (e?: FormEvent) => {
    e?.preventDefault();
    if (enviando) return;
    setError(null);
    if (esInstagram) {
      dudar('Por ahora solo analizamos webs y tiendas online. Usa el link de tu sitio o continúa sin web.');
      return;
    }
    const link = normalizarLink(valor);
    if (!link) {
      dudar('Ese link no parece de un sitio web. Un ejemplo válido: tumarca.com.');
      return;
    }
    setEnviando(true);
    setDicho(reaccionAlEnviar(dominioDe(link)));
    const empresa = await prepararEmpresa();
    if (!empresa.ok) {
      dudar(empresa.error);
      setEnviando(false);
      return;
    }
    // Una marca nueva limpia antes los borradores que dejaron intentos anteriores abandonados
    // (ADR-2026-10-28-borradores-de-marca-abandonados). No se espera: no frena el escaneo.
    if (nueva) void descartarBorradoresAbandonados(empresa.companyId);
    const inicio = await iniciarEscaneo(link, { marcaNueva: nueva });
    if (!inicio.ok) {
      dudar(inicio.error);
      setEnviando(false);
      return;
    }
    // Empezar de nuevo es empezar de cero: sin esto quedaban el lote y la marca de la vez
    // anterior, y las piezas se reenganchaban a un lote viejo.
    limpiarEstado();
    guardarEstado({
      companyId: empresa.companyId,
      origen: 'web',
      link: inicio.link,
      escaneoId: inicio.escaneoId,
      flujo,
    });
    navigate(siguiente);
  };

  const sinWeb = async () => {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    setDicho(REACCION_SIN_WEB);
    const empresa = await prepararEmpresa();
    if (!empresa.ok) {
      dudar(empresa.error);
      setEnviando(false);
      return;
    }
    if (nueva) void descartarBorradoresAbandonados(empresa.companyId);
    limpiarEstado();
    guardarEstado({ companyId: empresa.companyId, origen: 'manual', flujo });
    navigate(siguiente);
  };

  return (
    <div className="relative isolate min-h-[calc(100dvh-var(--om-barra))] overflow-hidden">
      <FondoCordillera quieto={quieto} />

      <div className="mx-auto flex min-h-[calc(100dvh-var(--om-barra))] max-w-[600px] flex-col gap-5 px-4 pb-[max(20px,env(safe-area-inset-bottom))] pt-6 md:gap-6 md:pb-10 md:pt-10">
        <GuiaCumbre
          tramos={tramosDelLink}
          letras={dialogo.letras}
          hablando={dialogo.hablando}
          terminado={dialogo.terminado}
          onCompletar={dialogo.completar}
          reaccion={reaccion}
          animo={animo}
          gesto={gesto}
          instantaneo={quieto}
        />

        <LoQueLee visibles={visibles} quietos={quieto} />

        {/* Las opciones esperan a que Cumbre termine de hablar. Van al pie, en el teléfono y
            en desktop (ADR-2026-10-02-onboarding-v2-guia-igual-en-desktop). */}
        {dialogo.terminado && (
          <div className="mt-auto flex flex-col gap-3 pt-2">
            <form onSubmit={enviar} noValidate className={`flex flex-col gap-3 ${quieto ? '' : 'om-aparece'}`}>
              <label htmlFor="link-de-marca" className="sr-only">
                Link de tu marca
              </label>
              <div className={`om-campo ${error ? 'is-error' : ''}`}>
                <div className="om-campo__dentro">
                  <svg
                    viewBox="0 0 24 24"
                    className={`h-5 w-5 shrink-0 transition-colors ${valor ? 'text-brand-primary' : 'text-[#98A1C0]'}`}
                    aria-hidden="true"
                  >
                    <path
                      d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    id="link-de-marca"
                    ref={inputRef}
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="tumarca.com"
                    value={valor}
                    onFocus={() => setEnfocado(true)}
                    onBlur={() => setEnfocado(false)}
                    onChange={(e) => {
                      setValor(e.target.value);
                      setError(null);
                      if (!enviando) setDicho(null);
                    }}
                    className="min-w-0 flex-1 bg-transparent text-[17px] font-semibold text-brand-black outline-none placeholder:font-normal placeholder:text-[#98A1C0]"
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? 'link-error' : plataforma ? 'link-plataforma' : undefined}
                  />
                  {plataforma && (
                    <span
                      key={plataforma}
                      id="link-plataforma"
                      className={`om-llega inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold ${
                        esInstagram ? 'bg-[#FFF7ED] text-[#9A3412]' : 'bg-[#ECFDF5] text-[#065F46]'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${esInstagram ? 'bg-[#F97316]' : 'bg-[#10B981]'}`} />
                      {ETIQUETA_CORTA[plataforma]}
                    </span>
                  )}
                </div>
              </div>
              {/* El error lo dice Cumbre en el globo (que ya lo anuncia); acá queda sólo para
                  el `aria-describedby` del campo. */}
              {error && (
                <p id="link-error" className="sr-only">
                  {error}
                </p>
              )}
              <div>
                <button
                  type="submit"
                  disabled={enviando || !valor.trim()}
                  className={`om-boton ${enviando ? 'is-enviando om-esqueleto-azul' : ''}`}
                >
                  {enviando ? 'Preparando todo…' : 'Analizar mi marca'}
                  {!enviando && <kbd className="hidden rounded bg-white/15 px-1.5 text-[11px] md:inline">⏎</kbd>}
                </button>
              </div>
            </form>

            <button
              type="button"
              onClick={() => void sinWeb()}
              disabled={enviando}
              className={`self-center rounded-full px-3 py-2 text-[14px] font-semibold text-[#4B5275] underline decoration-[#C5CCE6] underline-offset-4 transition-colors hover:text-brand-primary hover:decoration-brand-primary ${
                quieto ? '' : 'om-aparece-solo'
              }`}
            >
              {nueva ? 'No tiene web, la cargo yo' : 'No tengo web, la cargo yo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LinkDeMarca;
